import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";

const SlumTours = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const images = ["/images/Ki1.png", "/images/Ki2.png"];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const itinerary = [
    { icon: "📍", title: "Starting Location", desc: "Meet at Wabera Street just outside Carrefour St. Ellis. Your guide will brief you on the tour, its purpose, and the community guidelines before you begin.", meta: "10 min · Free Entry" },
    { icon: "🚶", title: "Kibera Slum Walk", desc: "Walk alongside local guides who call Kibera home. Explore daily life, thriving small enterprises, community schools, local markets, art spaces, and grassroots projects that define this remarkable neighbourhood.", meta: "2.5 hours · Guided Walking Tour" },
    { icon: "🤝", title: "Community Engagement", desc: "Interact with residents, artisans, and social entrepreneurs. This is an exchange — not a spectacle. Leave with a genuine understanding of resilience and community spirit.", meta: "Included in tour" },
    { icon: "🏠", title: "Return Point", desc: "Arrive back at Wabera Street, or your hotel if arranged in advance.", meta: "15 min · Free Entry" },
  ];

  const values = [
    { icon: "🧭", title: "Community-Led", desc: "Every guide lives in Kibera. Your money stays in the community." },
    { icon: "🎨", title: "Arts & Culture", desc: "Discover street art, music, and creativity born from resilience." },
    { icon: "🌱", title: "Grassroots Projects", desc: "Visit schools, social enterprises, and youth initiatives." },
    { icon: "📸", title: "Respectful Tourism", desc: "Photography only with explicit consent. Dignity first, always." },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');

        :root {
          --gold: #C9A84C;
          --gold-light: #E8C97A;
          --dark: #0A0A0A;
          --charcoal: #141414;
        }

        .st-root {
          font-family: 'Jost', sans-serif;
          background: var(--dark);
          color: #fff;
          min-height: 100vh;
        }

        /* ── HERO ── */
        .st-hero {
          position: relative;
          min-height: 88vh;
          display: flex;
          align-items: flex-end;
          overflow: hidden;
        }
        .st-hero-bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          transition: opacity 1.8s ease-in-out;
        }
        .st-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to top,
            rgba(10,10,10,1) 0%,
            rgba(10,10,10,0.6) 45%,
            rgba(10,10,10,0.3) 100%
          );
        }
        .st-hero-content {
          position: relative;
          z-index: 2;
          padding: 4rem 1.5rem 5rem;
          max-width: 860px;
          animation: fadeUp 1.2s ease both;
        }
        @keyframes fadeUp {
          from { opacity:0; transform:translateY(32px); }
          to   { opacity:1; transform:translateY(0); }
        }
        .st-eyebrow {
          font-size: 0.68rem;
          letter-spacing: 5px;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 1rem;
        }
        .st-hero-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2.8rem, 7vw, 5.5rem);
          font-weight: 700;
          line-height: 1.05;
          color: #fff;
          margin-bottom: 0.4rem;
        }
        .st-hero-title em { font-style: italic; color: var(--gold-light); }
        .st-hero-divider {
          width: 55px;
          height: 2px;
          background: var(--gold);
          margin: 1.4rem 0;
        }
        .st-hero-desc {
          font-size: clamp(0.95rem, 2vw, 1.1rem);
          font-weight: 300;
          color: rgba(255,255,255,0.78);
          line-height: 1.9;
          max-width: 620px;
          margin-bottom: 2.2rem;
        }
        .st-hero-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 2.5rem;
        }
        .st-pill {
          border: 1px solid rgba(201,168,76,0.45);
          color: var(--gold-light);
          padding: 0.3rem 1rem;
          border-radius: 50px;
          font-size: 0.72rem;
          letter-spacing: 2px;
          text-transform: uppercase;
          font-weight: 500;
        }
        .st-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: var(--gold);
          color: #0A0A0A;
          border: none;
          padding: 0.9rem 2.6rem;
          font-family: 'Jost', sans-serif;
          font-weight: 600;
          font-size: 0.82rem;
          letter-spacing: 3px;
          text-transform: uppercase;
          border-radius: 3px;
          cursor: pointer;
          transition: background 0.3s, transform 0.2s;
          text-decoration: none;
        }
        .st-btn:hover { background: var(--gold-light); transform: translateY(-2px); color: #0A0A0A; }

        /* ── STATS ── */
        .st-stats {
          background: var(--charcoal);
          border-top: 1px solid rgba(201,168,76,0.2);
          border-bottom: 1px solid rgba(201,168,76,0.2);
          padding: 1.8rem 1rem;
        }
        .st-stat { text-align: center; padding: 0.4rem 1rem; }
        .st-stat-n {
          font-family: 'Cormorant Garamond', serif;
          font-size: 2.2rem;
          font-weight: 700;
          color: var(--gold);
          line-height: 1;
        }
        .st-stat-l {
          font-size: 0.65rem;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.4);
          margin-top: 0.2rem;
        }

        /* ── GALLERY ── */
        .st-gallery {
          padding: 5rem 0 4rem;
          background: var(--dark);
        }
        .st-label {
          font-size: 0.65rem;
          letter-spacing: 5px;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 0.6rem;
        }
        .st-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(1.8rem, 4vw, 2.8rem);
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.4rem;
        }
        .st-line {
          width: 40px;
          height: 2px;
          background: var(--gold);
          margin-bottom: 2.5rem;
        }
        .st-img-frame {
          position: relative;
          border-radius: 8px;
          overflow: hidden;
          aspect-ratio: 16/9;
          max-width: 760px;
          margin: 0 auto;
          box-shadow: 0 30px 80px rgba(0,0,0,0.6);
          border: 1px solid rgba(201,168,76,0.15);
        }
        .st-img-frame img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: opacity 1.4s ease-in-out;
        }
        .st-dots {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 1.4rem;
        }
        .st-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(255,255,255,0.25);
          border: none;
          cursor: pointer;
          transition: background 0.3s, transform 0.3s;
          padding: 0;
        }
        .st-dot.active { background: var(--gold); transform: scale(1.4); }

        /* ── NOTICE BAND ── */
        .st-notice {
          background: rgba(201,168,76,0.06);
          border-top: 1px solid rgba(201,168,76,0.2);
          border-bottom: 1px solid rgba(201,168,76,0.2);
          padding: 2.5rem 1.5rem;
          text-align: center;
        }
        .st-notice p {
          font-size: 0.92rem;
          color: rgba(255,255,255,0.65);
          max-width: 640px;
          margin: 0 auto;
          line-height: 1.85;
          font-weight: 300;
        }
        .st-notice strong { color: var(--gold-light); font-weight: 500; }

        /* ── VALUES GRID ── */
        .st-values {
          background: var(--charcoal);
          border-top: 1px solid rgba(201,168,76,0.12);
          padding: 5rem 0;
        }
        .st-value-card {
          background: rgba(201,168,76,0.05);
          border: 1px solid rgba(201,168,76,0.15);
          border-radius: 8px;
          padding: 2rem 1.5rem;
          text-align: center;
          height: 100%;
          transition: border-color 0.3s, transform 0.3s;
        }
        .st-value-card:hover {
          border-color: rgba(201,168,76,0.5);
          transform: translateY(-5px);
        }
        .st-value-icon {
          font-size: 2.2rem;
          display: block;
          margin-bottom: 0.9rem;
        }
        .st-value-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--gold-light);
          margin-bottom: 0.4rem;
        }
        .st-value-desc {
          font-size: 0.82rem;
          color: rgba(255,255,255,0.5);
          line-height: 1.65;
        }

        /* ── ITINERARY ── */
        .st-itinerary {
          background: var(--dark);
          padding: 5rem 0 6rem;
          border-top: 1px solid rgba(201,168,76,0.12);
        }
        .st-timeline {
          max-width: 720px;
          margin: 0 auto;
          position: relative;
        }
    
        .st-tl-item {
          display: flex;
          gap: 1.8rem;
          margin-bottom: 2rem;
        }
        .st-tl-item:last-child { margin-bottom: 0; }
        .st-tl-dot {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: rgba(201,168,76,0.08);
          border: 1px solid rgba(201,168,76,0.35);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 1.3rem;
          z-index: 1;
        }
        .st-tl-body {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(201,168,76,0.1);
          border-radius: 6px;
          padding: 1.2rem 1.5rem;
          flex: 1;
          transition: border-color 0.3s;
        }
        .st-tl-body:hover { border-color: rgba(201,168,76,0.4); }
        .st-tl-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.15rem;
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.3rem;
        }
        .st-tl-desc {
          font-size: 0.85rem;
          font-weight: 300;
          color: rgba(255,255,255,0.55);
          line-height: 1.75;
          margin-bottom: 0.5rem;
        }
        .st-tl-meta {
          font-size: 0.62rem;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: var(--gold);
          opacity: 0.8;
        }

        /* ── CTA ── */
        .st-cta {
          background: linear-gradient(135deg, #1a1200 0%, #2a1f00 50%, #1a1200 100%);
          border-top: 1px solid rgba(201,168,76,0.2);
          padding: 5rem 1.5rem;
          text-align: center;
        }
        .st-cta h2 {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 700;
          color: #fff;
          margin-bottom: 1rem;
        }
        .st-cta p {
          font-size: 0.92rem;
          color: rgba(255,255,255,0.55);
          max-width: 480px;
          margin: 0 auto 2.5rem;
          line-height: 1.85;
        }

        @media (max-width: 576px) {
          .st-hero-content { padding: 3rem 1.2rem 4rem; }
          .st-timeline::before { left: 18px; }
          .st-tl-dot { width: 36px; height: 36px; font-size: 1.1rem; }
        }
      `}</style>

      <div className="st-root">

        {/* HERO */}
        <section className="st-hero">
          {images.map((img, i) => (
            <div
              key={i}
              className="st-hero-bg"
              style={{ backgroundImage: `url(${img})`, opacity: i === currentImage ? 1 : 0 }}
            />
          ))}
          <div className="st-hero-overlay" />
          <div className="container">
            <div className="st-hero-content">
              <p className="st-eyebrow">Nairobi · Kenya</p>
              <h1 className="st-hero-title">
                Kibera <em>Slum</em><br />Tour
              </h1>
              <div className="st-hero-divider" />
              <p className="st-hero-desc">
                Step into the vibrant heart of Nairobi's Kibera — a community known for
                its extraordinary strength, creativity, and spirit. Walk with local guides
                who live here, and leave with a changed perspective.
              </p>
              <div className="st-hero-pills">
                <span className="st-pill">3 Hours</span>
                <span className="st-pill">$30 Per Person</span>
                <span className="st-pill">Local Guides</span>
                <span className="st-pill">Community-Led</span>
              </div>
              <button className="st-btn" onClick={() => window.location.href = "/tours"}>
                Book This Tour
              </button>
            </div>
          </div>
        </section>

        {/* STATS */}
        <div className="st-stats">
          <div className="container">
            <div className="row justify-content-center">
              {[
                { n: "3", l: "Hours" },
                { n: "$30", l: "Per Person" },
                { n: "100%", l: "Local Guides" },
                { n: "5★", l: "Rated" },
              ].map((s, i) => (
                <div key={i} className="col-6 col-md-3">
                  <div className="st-stat">
                    <div className="st-stat-n">{s.n}</div>
                    <div className="st-stat-l">{s.l}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* GALLERY */}
        <section className="st-gallery">
          <div className="container">
            <p className="st-label">Experience Gallery</p>
            <h2 className="st-title">Life in Kibera</h2>
            <div className="st-line" />
            <div className="st-img-frame">
              {images.map((img, i) => (
                <img key={i} src={img} alt={`Kibera ${i + 1}`} style={{ opacity: i === currentImage ? 1 : 0 }} />
              ))}
            </div>
            <div className="st-dots">
              {images.map((_, i) => (
                <button key={i} className={`st-dot ${i === currentImage ? "active" : ""}`} onClick={() => setCurrentImage(i)} />
              ))}
            </div>
          </div>
        </section>

        {/* NOTICE */}
        <div className="st-notice">
          <p>
            This is <strong>responsible, community-led tourism</strong>. Our guides are Kibera residents.
            Revenue supports local livelihoods and grassroots initiatives. We ask all visitors to
            approach this experience with <strong>respect, curiosity, and an open mind.</strong>
          </p>
        </div>

        {/* VALUES */}
        <section className="st-values">
          <div className="container">
            <p className="st-label" style={{ textAlign: "center" }}>Our Principles</p>
            <h2 className="st-title" style={{ textAlign: "center" }}>How We Do It</h2>
            <div className="st-line" style={{ margin: "0 auto 2.5rem" }} />
            <div className="row g-4">
              {values.map((v, i) => (
                <div key={i} className="col-6 col-md-3">
                  <div className="st-value-card">
                    <span className="st-value-icon">{v.icon}</span>
                    <div className="st-value-title">{v.title}</div>
                    <div className="st-value-desc">{v.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ITINERARY */}
        <section className="st-itinerary">
          <div className="container">
            <p className="st-label" style={{ textAlign: "center" }}>Your Journey</p>
            <h2 className="st-title" style={{ textAlign: "center" }}>Tour Itinerary</h2>
            <div className="st-line" style={{ margin: "0 auto 3rem" }} />
            <div className="st-timeline">
              {itinerary.map((item, i) => (
                <div key={i} className="st-tl-item">
                  <div className="st-tl-dot">{item.icon}</div>
                  <div className="st-tl-body">
                    <div className="st-tl-title">{item.title}</div>
                    <div className="st-tl-desc">{item.desc}</div>
                    <div className="st-tl-meta">{item.meta}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="st-cta">
          <h2>Experience Kibera's Spirit</h2>
          <p>
            An unforgettable, eye-opening experience that changes perspectives
            and supports community-led initiatives. Book your tour today.
          </p>
          <button className="st-btn" onClick={() => window.location.href = "/tours"}>
            Book Your Experience
          </button>
        </section>

      </div>
    </>
  );
};

export default SlumTours;
